<template>
  <div class="login-container">
    <!-- Toast提示组件 -->
    <div v-if="toast.show" class="toast-container">
      <div class="toast" :class="'toast-' + toast.type">
        <div class="toast-message">{{ toast.message }}</div>
      </div>
    </div>
    
    <div class="login-box">
      <div class="login-tabs">
        <div class="tab-item" :class="{ active: activeTab === 'email' }" @click="switchTab('email')">
          邮箱登录/注册
        </div>
        <div class="tab-item" :class="{ active: activeTab === 'wechat' }" @click="switchTab('wechat')">
          微信扫码登录
        </div>
      </div>

      <!-- 面板：微信扫码登录 -->
      <div class="tab-panel" v-if="activeTab === 'wechat'">
        <h2 class="login-title">微信扫码登录</h2>
        <div id="wx-qrcode" class="qrcode-container"></div>
        <div class="wechat-tip">请使用微信扫一扫，完成授权登录。<span style="color: red;"></span></div>
      </div>

      <!-- 面板：邮箱登录/注册 -->
      <div class="tab-panel" v-else>
        <h2 class="login-title">{{ isLogin ? '邮箱登录' : '邮箱注册' }}</h2>
        
        <!-- 登录表单 -->
        <form v-if="isLogin && !isForgot" class="email-form" @submit.prevent="handleLogin">
          <div class="form-item">
            <input 
              type="email" 
              v-model="loginForm.email" 
              placeholder="请输入邮箱" 
              class="form-input"
              @blur="saveLastEmail"
              @input="errorMessage = ''"
              key="login-email"
            />
          </div>
          <div class="form-item">
            <input 
              type="password" 
              v-model="loginForm.password" 
              placeholder="请输入密码" 
              class="form-input"
              @keyup.enter="handleLogin"
              key="login-password"
            />
          </div>
          <div class="form-item remember-row">
            <label class="remember-label">
              <input type="checkbox" v-model="rememberPassword" /> 记住密码
            </label>
          </div>
          <!-- 错误信息展示 -->
          <div v-if="errorMessage" class="error-banner">{{ errorMessage }}</div>
          <div class="form-item">
            <button type="submit" class="form-button">登录</button>
          </div>
          <div class="form-footer">
            <span @click="isForgot = true" class="form-link" style="margin-right: 10px;">忘记密码？</span>
            <span @click="toggleAuthMode(false)" class="form-link">没有账号？去注册</span>
          </div>
        </form>

        <!-- 忘记密码表单 -->
        <div v-else-if="isLogin && isForgot" class="email-form">
          <div class="form-item">
            <input type="email" v-model="forgotForm.email" placeholder="请输入邮箱" class="form-input" key="forgot-email" @input="errorMessage = ''; forgotForm.code = ''" />
          </div>
          <div class="form-item verification-code">
            <input type="text" v-model="forgotForm.code" placeholder="请输入验证码" class="form-input" key="forgot-code" @input="errorMessage = ''" autocomplete="off" />
            <button 
              @click="sendResetCode" 
              class="code-button"
              :class="{
                'sending': sendingCode,
                'countdown': codeStatus === 'countdown',
                'resend': codeStatus === 'resend'
              }"
              :disabled="cooldown > 0 || sendingCode"
            >
              <span v-if="sendingCode">发送中...</span>
              <span v-else-if="cooldown > 0">发送成功,{{ cooldown }}秒后可重新发送</span>
              <span v-else-if="codeStatus === 'resend'">重新发送</span>
              <span v-else>发送验证码</span>
            </button>
          </div>
          <div class="form-item">
            <input type="password" v-model="forgotForm.password" placeholder="请输入新密码" class="form-input" key="forgot-password" />
          </div>
          <div v-if="errorMessage" class="error-banner">{{ errorMessage }}</div>
          <div class="form-item">
            <button type="button" class="form-button" @click="handleReset">重置密码</button>
          </div>
          <div class="form-footer">
            <span @click="isForgot = false" class="form-link">返回登录</span>
          </div>
        </div>

        <!-- 注册表单 -->
        <div v-else class="email-form">
          <div class="form-item">
            <input 
              type="email" 
              v-model="registerForm.email" 
              placeholder="请输入邮箱" 
              class="form-input"
              key="register-email"
              @input="errorMessage = ''; registerForm.code = ''"
            />
          </div>
          <div class="form-item verification-code">
            <input 
              type="text" 
              v-model="registerForm.code" 
              placeholder="请输入验证码" 
              class="form-input"
              key="register-code"
              autocomplete="off"
            />
            <button 
              @click="sendVerificationCode" 
              class="code-button"
              :class="{
                'sending': sendingCode,
                'countdown': codeStatus === 'countdown',
                'resend': codeStatus === 'resend'
              }"
              :disabled="cooldown > 0 || sendingCode"
            >
              <span v-if="sendingCode">发送中...</span>
              <span v-else-if="cooldown > 0">发送成功,{{ cooldown }}秒后可重新发送</span>
              <span v-else-if="codeStatus === 'resend'">重新发送</span>
              <span v-else>获取验证码</span>
            </button>
          </div>
          <div class="form-item">
            <input 
              type="password" 
              v-model="registerForm.password" 
              placeholder="请输入密码" 
              class="form-input"
              key="register-password"
            />
          </div>
          <div class="form-item">
            <input 
              type="password" 
              v-model="registerForm.confirmPassword" 
              placeholder="请确认密码" 
              class="form-input"
              key="register-confirm-password"
            />
          </div>
          <div class="form-item">
            <input 
              type="text" 
              v-model="registerForm.nickname" 
              placeholder="请输入昵称" 
              class="form-input"
              @blur="checkNicknameExists"
              key="register-nickname"
            />
          </div>
          <div v-if="nicknameStatus==='taken'" class="error-banner">该昵称已被使用，请更换</div>
          <div v-else-if="nicknameStatus==='error'" class="error-banner">检查昵称失败，请稍后重试</div>
          <!-- 错误信息展示 -->
          <div v-if="errorMessage" class="error-banner">{{ errorMessage }}</div>
          <div class="form-item">
            <button @click="handleRegister" class="form-button">注册</button>
          </div>
          <div class="form-footer">
            <span @click="toggleAuthMode(true)" class="form-link">已有账号？去登录</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  import axios from 'axios'
  

  export default {
    data() {
      return {
        // 界面控制
        isLogin: true,      // 默认显示登录表单
        isForgot: false,
        activeTab: 'email', // 选项卡：email | wechat
        wechatQrInited: false, // 是否已初始化微信二维码
        
        // 登录表单
        loginForm: {
          email: '',
          password: ''
        },
        
        // 忘记密码表单
        forgotForm: {
          email: '',
          code: '',
          password: ''
        },
        
        // 注册表单
        registerForm: {
          email: '',
          code: '',
          password: '',
          confirmPassword: '',
          nickname: ''
        },
        checkingNickname: false,
        nicknameStatus: '',
        rememberPassword: false,
        
        // 验证码冷却时间
        cooldown: 0,
        
        // 验证码按钮状态：initial(初始), sending(发送中), countdown(倒计时), resend(可重新发送)
        codeStatus: 'initial',
        
        // 是否正在发送验证码
        sendingCode: false,
        
        // 错误信息
        errorMessage: '',
        
        // 加载状态
        loading: false,
        
        // Toast提示
        toast: {
          show: false,
          message: '',
          type: 'success', // success, danger, warning, info
          timeout: null
        }
      }
    },
    components: {
    },
    mounted() {
      // 检查是否是从微信回调回来的 (前端接管回调)
      const code = this.$route.query.code
      const state = this.$route.query.state
      if (code) {
        this.handleWeChatCallback(code, state)
      }

      const lastEmail = localStorage.getItem('last_login_email')
      if (lastEmail) {
        this.loginForm.email = lastEmail
      }
      const remember = localStorage.getItem('remember_password')
      this.rememberPassword = remember === '1' || remember === 'true'
      if (this.rememberPassword) {
        const lastPwd = localStorage.getItem('last_login_password') || ''
        this.loginForm.password = lastPwd
      }
    },
    methods: {
      // 切换选项卡
      switchTab(tab) {
        this.activeTab = tab
        if (tab === 'wechat') {
            this.$nextTick(() => {
                this.initWeChatQr()
            })
        }
      },
      // 切换登录/注册模式
      toggleAuthMode(isLogin) {
        if (!isLogin) {
          // 登录 -> 注册：同步邮箱，清空其他敏感/验证字段
          this.registerForm.email = this.loginForm.email;
          this.registerForm.code = '';
          this.registerForm.password = '';
          this.registerForm.confirmPassword = '';
          this.registerForm.nickname = '';
        } else {
          // 注册 -> 登录：同步邮箱
          this.loginForm.email = this.registerForm.email;
          this.loginForm.password = '';
        }
        this.isLogin = isLogin;
        this.errorMessage = ''; // 切换时清除错误信息
        this.nicknameStatus = '';
      },
      saveLastEmail() {
        if (this.loginForm && this.loginForm.email) {
          localStorage.setItem('last_login_email', this.loginForm.email)
        }
      },

      // 初始化微信扫码二维码（网站扫码登录）
      async initWeChatQr() {
        try {
          // 动态加载wxLogin脚本
          await this.loadWxLoginScript()
          const appid = process.env.VUE_APP_WECHAT_QR_APPID
          if (!appid) {
            this.showToast('danger', '未配置微信网站应用APPID (VUE_APP_WECHAT_QR_APPID)')
            return
          }
          const origin = window.location.origin
          const redirectPath = window.location.pathname
          const invite_code = this.$route.query.invite_code || localStorage.getItem('invite_code') || ''
          const redirectUri = encodeURIComponent(`${origin}${redirectPath}`)
          
          // 简化 state：直接使用邀请码，这是微信最稳定的传参方式
          const state = invite_code || 'none'

          // 使用微信“网站应用扫码登录”组件
          // 如果没有网站应用APPID，二维码会显示但最终跳转会失败，请配置开放平台网站应用。
          /* global WxLogin */
          new WxLogin({
            id: 'wx-qrcode',
            appid,
            scope: 'snsapi_login',
            redirect_uri: redirectUri,
            state,
            // 下面参数可选：样式与二维码大小
            style: 'black',
            href: ''
          })
          this.wechatQrInited = true
        } catch (e) {
          console.error('初始化微信二维码失败', e)
          this.showToast('danger', '加载微信二维码失败，请稍后重试')
        }
      },

      // 动态加载微信登录脚本
      loadWxLoginScript() {
        return new Promise((resolve, reject) => {
          if (window.WxLogin) return resolve()
          const script = document.createElement('script')
          script.src = 'https://res.wx.qq.com/connect/zh_CN/htmledition/js/wxLogin.js'
          script.onload = () => resolve()
          script.onerror = () => reject(new Error('wxLogin.js 加载失败'))
          document.body.appendChild(script)
        })
      },

      // 显示Toast提示
      showToast(type, message, duration = 3000) {
        // 清除之前的定时器
        if (this.toast.timeout) {
          clearTimeout(this.toast.timeout);
        }
        
        // 设置Toast内容
        this.toast.show = true;
        this.toast.type = type;
        this.toast.message = message;
        
        // 设置自动关闭
        this.toast.timeout = setTimeout(() => {
          this.toast.show = false;
        }, duration);
      },
      
      // 发起微信网页授权（在微信内置浏览器中使用）
      startWeChatLogin() {
        try {
          const appid = 'wx677bc170ee9f2ae1' // 服务号AppID
          const origin = window.location.origin
          const redirectPath = window.location.pathname
          const invite_code = this.$route.query.invite_code || localStorage.getItem('invite_code') || ''
          const redirectUri = encodeURIComponent(`${origin}${redirectPath}`)
          
          // 简化 state
          const state = invite_code || 'none'
          
          const scope = 'snsapi_userinfo' // 需要用户昵称头像等信息
          const url = `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${appid}&redirect_uri=${redirectUri}&response_type=code&scope=${scope}&state=${state}#wechat_redirect`
          window.location.href = url
        } catch (e) {
          console.error('发起微信授权失败', e)
          this.showToast('danger', '发起微信授权失败，请稍后重试')
        }
      },
      
      // 处理微信回调换取 Token
      async handleWeChatCallback(code, state) {
        // 防止重复触发
        if (this.loading) return
        this.loading = true
        
        try {
          this.showToast('info', '微信登录中...')
          // 调用后端换票接口 (增加 format=json 强制返回数据)
          const response = await axios.get('https://api.zizizaizai.com/v2/login/wx/callback', {
            params: { code, state, format: 'json' }
          })
          
          let finalToken = ''
          let finalRefreshToken = ''
          
          if (response.data && response.data.code === 20000) {
            finalToken = response.data.data.access_token
            finalRefreshToken = response.data.data.refresh_token
          } else {
            // 兜底逻辑：尝试从可能发生的重定向 URL 中提取
            const url = response.request.responseURL || ''
            if (url.includes('token=')) {
              const urlObj = new URL(url)
              finalToken = urlObj.searchParams.get('token')
              finalRefreshToken = urlObj.searchParams.get('refresh_token')
            }
          }

          if (finalToken) {
            localStorage.setItem('token', finalToken)
            localStorage.setItem('refresh_token', finalRefreshToken)
            this.showToast('success', '登录成功')
            
            // 清理 URL 参数
            this.$router.replace(this.$route.path)
            
            // 跳转
            setTimeout(() => {
              this.$router.push('/')
            }, 500)
          } else {
            this.showToast('danger', '获取登录凭证失败')
          }
        } catch (e) {
          console.error('微信登录异常', e)
          this.showToast('danger', '微信登录异常，请重试')
        } finally {
          this.loading = false
        }
      },
      
      // 发送验证码
      async sendVerificationCode() {
        // 如果已经在发送中或倒计时中，则不再重复发送
        if (this.sendingCode || this.cooldown > 0) {
          return;
        }
        
        // 邮箱格式验证
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(this.registerForm.email)) {
          this.errorMessage = '请输入有效的邮箱地址';
          this.showToast('danger', this.errorMessage);
          return;
        }
        
        // 设置按钮为加载状态
        this.sendingCode = true;
        
        try {
          const response = await axios.post('https://api.zizizaizai.com/v2/login/email/send_code', {
            email: this.registerForm.email
          });
          // console.log('发送验证码响应:', response.data);
          if (response.data.code === 20000 || response.data.code === 0) {
            // 发送成功提示
            // console.log('发送验证码成功:', response.data.message);
            // 使用Bootstrap toast提示
            this.showToast('success', response.data.message || '验证码发送成功，请查收邮件');
            
            // 开始倒计时
            this.cooldown = 60;
            this.codeStatus = 'countdown'; // 设置状态为倒计时
            
            const timer = setInterval(() => {
              this.cooldown--;
              if (this.cooldown <= 0) {
                clearInterval(timer);
                this.codeStatus = 'resend'; // 设置状态为可重新发送
              }
            }, 1000);
          } else {
            // 发送失败
              this.errorMessage = response.data.message || '验证码发送失败';
              this.showToast('danger', this.errorMessage);
              this.codeStatus = 'initial'; // 重置状态
          }
        } catch (error) {
          console.error('发送验证码失败:', error);
            this.errorMessage = (error.response && error.response.data && error.response.data.message) || '网络错误，请稍后重试';
            this.showToast('danger', this.errorMessage);
            this.codeStatus = 'initial'; // 重置状态
        } finally {
          this.sendingCode = false; // 无论成功失败，都取消加载状态
        }
      },
      
      // 注册
      async handleRegister() {
        // 表单验证
        if (!this.registerForm.email || !this.registerForm.code || 
            !this.registerForm.password || !this.registerForm.confirmPassword || 
            !this.registerForm.nickname) {
          this.errorMessage = '请填写完整注册信息';
          this.showToast('danger', this.errorMessage);
          return;
        }
        
        if (this.registerForm.password !== this.registerForm.confirmPassword) {
          this.errorMessage = '两次输入的密码不一致';
          this.showToast('danger', this.errorMessage);
          return;
        }
        // 注释掉此处的硬性阻断，允许在检查中提交注册，由后端进行兜底校验
        // if (this.checkingNickname) {
        //   this.errorMessage = '正在检查昵称，请稍后再试';
        //   this.showToast('warning', this.errorMessage);
        //   return;
        // }
        if (this.nicknameStatus === 'taken') {
          this.errorMessage = '该昵称已被使用，请更换';
          this.showToast('danger', this.errorMessage);
          return;
        }
        
        try {
          const invite_code = localStorage.getItem('invite_code') || '';
          const response = await axios.post('https://api.zizizaizai.com/v2/login/email/register', {
            email: this.registerForm.email,
            code: this.registerForm.code,
            password: this.registerForm.password,
            nickname: this.registerForm.nickname,
            invite_code: invite_code
          });
          
          if (response.data.code === 20000) {
            // 注册成功
            this.showToast('success', '注册成功，正在为您自动登录...');
            
            // 将注册信息同步到登录表单
            this.loginForm.email = this.registerForm.email;
            this.loginForm.password = this.registerForm.password;
            
            // 延迟3秒自动执行登录，给用户看提示的时间
            setTimeout(() => {
              this.handleLogin();
            }, 3000);
          } else {
            // 注册失败
            this.errorMessage = response.data.message || '注册失败';
            this.showToast('danger', this.errorMessage);
          }
        } catch (error) {
          console.error('注册失败:', error);
          this.errorMessage = (error.response && error.response.data && error.response.data.message) || '网络错误，请稍后重试';
          this.showToast('danger', this.errorMessage);
        }
      },
      async checkNicknameExists() {
        const nick = (this.registerForm && this.registerForm.nickname || '').trim()
        if (!nick) {
          this.nicknameStatus = ''
          return
        }
        this.checkingNickname = true
        this.nicknameStatus = ''
        try {
          const rs = await axios.get('https://api.zizizaizai.com/user/check_nickname', { params: { nickname: nick }, timeout: 3000 })
          const ok = rs && rs.data && (rs.data.code === 20000 || rs.data.code === 0)
          const exists = rs && rs.data && rs.data.data ? !!rs.data.data.exists : false
          if (ok) {
            this.nicknameStatus = exists ? 'taken' : 'available'
          } else {
            this.nicknameStatus = 'error'
          }
        } catch (e) {
          this.nicknameStatus = 'error'
        } finally {
          this.checkingNickname = false
        }
      },
      
      // 登录
      async handleLogin() {
        // 表单验证
        if (!this.loginForm.email || !this.loginForm.password) {
          this.errorMessage = '请输入邮箱和密码';
          this.showToast('danger', this.errorMessage);
          return;
        }
        
        try {
          // 这里需要根据实际接口调整
          const response = await axios.post('https://api.zizizaizai.com/v2/login/email/login', {
            email: this.loginForm.email,
            password: this.loginForm.password
          });
          
          if (response.data.code === 20000) {
            // 登录成功，保存token并跳转到来源页
            const d = response.data.data || {}
            const accessToken = d.access_token || d.token
            const refreshToken = d.refresh_token
            const userInfo = d.user_info || null
            if (accessToken) {
              localStorage.setItem('token', accessToken)
            }
            if (refreshToken) {
              localStorage.setItem('refresh_token', refreshToken)
            }
            if (userInfo) {
              try { localStorage.setItem('user_info', JSON.stringify(userInfo)) } catch (e) { console.warn('save user_info failed', e) }
              try { window.dispatchEvent(new CustomEvent('user-login', { detail: { userInfo } })) } catch (e) { console.warn('dispatch user-login failed', e) }
            } else {
              try { window.dispatchEvent(new CustomEvent('user-login', { detail: { userInfo: null } })) } catch (e) { console.warn('dispatch user-login failed', e) }
            }
            localStorage.setItem('last_login_email', this.loginForm.email)
            localStorage.setItem('remember_password', this.rememberPassword ? '1' : '0')
            if (this.rememberPassword) {
              localStorage.setItem('last_login_password', this.loginForm.password)
            } else {
              localStorage.removeItem('last_login_password')
            }
            const redirect = (this.$route && this.$route.query && this.$route.query.redirect) ? this.$route.query.redirect : '/'
            this.$router.replace(redirect)
          } else {
            // 登录失败
            this.errorMessage = response.data.message || '登录失败';
            this.showToast('danger', this.errorMessage);
          }
        } catch (error) {
          console.error('登录失败:', error);
          this.errorMessage = (error.response && error.response.data && error.response.data.message) || '网络错误，请稍后重试';
          this.showToast('danger', this.errorMessage);
        }
      },

      async sendResetCode() {
        if (this.sendingCode || this.cooldown > 0) {
          return;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(this.forgotForm.email)) {
          this.errorMessage = '请输入有效的邮箱地址';
          this.showToast('danger', this.errorMessage);
          return;
        }
        this.sendingCode = true;
        try {
          const rs = await axios.post('https://api.zizizaizai.com/v2/login/email/reset/send_code', { email: this.forgotForm.email })
          if (rs.data && (rs.data.code === 20000 || rs.data.code === 0)) {
            this.showToast('success', rs.data.message || '验证码已发送');
            this.cooldown = 60;
            this.codeStatus = 'countdown';
            const timer = setInterval(() => {
              this.cooldown--;
              if (this.cooldown <= 0) {
                clearInterval(timer);
                this.codeStatus = 'resend';
              }
            }, 1000);
          } else {
            this.errorMessage = (rs.data && rs.data.message) || '发送失败';
            this.showToast('danger', this.errorMessage);
            this.codeStatus = 'initial';
          }
        } catch (error) {
          this.errorMessage = (error.response && error.response.data && error.response.data.message) || '网络错误，请稍后重试';
          this.showToast('danger', this.errorMessage);
          this.codeStatus = 'initial';
        } finally {
          this.sendingCode = false;
        }
      },

      async handleReset() {
        if (!this.forgotForm.email || !this.forgotForm.code || !this.forgotForm.password) {
          this.errorMessage = '请输入完整信息';
          this.showToast('danger', this.errorMessage);
          return;
        }
        try {
          const rs = await axios.post('https://api.zizizaizai.com/v2/login/email/reset/confirm', {
            email: this.forgotForm.email,
            code: this.forgotForm.code,
            password: this.forgotForm.password
          })
          if (rs.data && rs.data.code === 20000) {
            this.showToast('success', '密码已重置');
            this.isForgot = false;
            this.isLogin = true;
            this.loginForm.email = this.forgotForm.email;
          } else {
            this.errorMessage = (rs.data && rs.data.message) || '重置失败';
            this.showToast('danger', this.errorMessage);
          }
        } catch (error) {
          this.errorMessage = (error.response && error.response.data && error.response.data.message) || '网络错误，请稍后重试';
          this.showToast('danger', this.errorMessage);
        }
      }
    }
  }
</script>

<style scoped>
.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background-color: var(--bg-main);
}

.login-box {
  width: 400px;
  padding: 30px;
  background-color: var(--bg-card);
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  text-align: center;
  border: 1px solid var(--border-color);
}

/* 登录标签样式 */
.login-tabs {
  display: flex;
  border-bottom: 1px solid var(--border-color);
  margin-bottom: 20px;
}

.tab-item {
  flex: 1;
  padding: 10px 0;
  text-align: center;
  cursor: pointer;
  color: var(--text-muted);
  font-size: 16px;
  transition: all 0.3s;
}

.tab-item.active {
  color: var(--accent-color);
  border-bottom: 2px solid var(--accent-color);
}

.tab-item:hover {
  color: var(--accent-color);
}

.tab-panel {
  min-height: 300px;
}

.login-title {
  margin-bottom: 20px;
  color: var(--text-main);
  font-weight: 500;
}

/* 微信登录样式 */
.qrcode-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 0;
  background: white; /* 微信二维码需要白色背景以便识别，但在黑夜模式下我们可以加个边框 */
  border-radius: 4px;
  margin: 0 20px;
}

.wechat-tip {
  margin-top: 15px;
  color: var(--text-muted);
  font-size: 14px;
}

/* 邮箱表单样式 */
.email-form {
  text-align: left;
}

.form-item {
  margin-bottom: 20px;
  position: relative;
}

.form-input {
  width: 100%;
  height: 40px;
  line-height: 40px;
  padding: 0 15px;
  border: 1px solid var(--border-color);
  background-color: var(--bg-card);
  border-radius: 4px;
  color: var(--text-main);
  transition: border-color 0.2s;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: var(--accent-color);
}

.form-button {
  width: 100%;
  height: 40px;
  background-color: var(--accent-color);
  border: none;
  border-radius: 4px;
  color: white;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.form-button:hover {
  opacity: 0.9;
}

.form-button:active {
  transform: translateY(1px);
}

.form-footer {
  text-align: center;
  margin-top: 10px;
}

.form-link {
  color: var(--accent-color);
  cursor: pointer;
}


.form-link:hover {
  text-decoration: underline;
}

/* 验证码输入框样式 */
.verification-code {
  display: flex;
}

.verification-code .form-input {
  flex: 1;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.code-button {
  width: 120px;
  height: 40px;
  background-color: #409EFF;
  border: none;
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
  color: white;
  cursor: pointer;
  transition: background-color 0.3s;
}

.code-button:hover {
  background-color: #66b1ff;
}

.code-button:disabled {
  background-color: #a0cfff;
  cursor: not-allowed;
}

.code-button.sending {
  background-color: #79bbff;
  position: relative;
}

.code-button.countdown {
  background-color: #909399;
}

.code-button.resend {
  background-color: #67c23a;
}

/* Toast提示样式 */
.toast-container {
  position: fixed;
  top: 40px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
}

.toast {
  padding: 12px 20px;
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin-bottom: 10px;
  animation: fadeIn 0.3s ease;
  min-width: 250px;
}

.toast-success {
  background-color: #d4edda;
  color: #155724;
  border-left: 4px solid #28a745;
}

.toast-danger {
  background-color: #f8d7da;
  color: #721c24;
  border-left: 4px solid #dc3545;
}

.toast-warning {
  background-color: #fff3cd;
  color: #856404;
  border-left: 4px solid #ffc107;
}

.toast-info {
  background-color: #d1ecf1;
  color: #0c5460;
  border-left: 4px solid #17a2b8;
}

.toast-message {
  font-size: 14px;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* 调整微信登录组件样式 - 使用::v-deep替代/deep/ */
::v-deep(.impowerBox) {
  width: 300px !important;
  height: auto !important;
  border: none !important;
  margin: 0 auto !important;
}

::v-deep(.impowerBox .qrcode) {
  width: 200px !important;
  height: 200px !important;
  margin: 0 auto !important;
}

::v-deep(.impowerBox .title) {
  display: none !important;
}

::v-deep(.impowerBox .info) {
  display: none !important;
}

/* 响应式设计 */
@media screen and (max-width: 768px) {
  .login-box {
    width: 90%;
    max-width: 400px;
  }
  
  .code-button {
    width: 100px;
    font-size: 12px;
  }
}

::v-deep(.impowerBox .status) {
  text-align: center !important;
}

/* 错误信息展示样式 */
.error-banner {
  background-color: #f8d7da; /* 与 toast-danger 背景一致 */
  color: #721c24;
  border-left: 4px solid #dc3545;
  padding: 10px 12px;
  border-radius: 4px;
  margin-bottom: 10px;
  font-size: 14px;
}
</style>
